import { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { EyeClosed } from '../icons/eye-closed'
import { Notification } from '../icons/notification'
import { Eye } from '../icons/eye'
import { styles, colors, texts } from './header-dashboard.styles'
import { logOut } from '../../features/authentication/presentation/redux/actions/log-out-action'
import { paths } from '../../router/paths'
import type { RootState } from '../../redux/global-store'
import { getJourneyRequest } from '../../features/journey/presentation/redux/actions/journey-actions'
import { getCurrentUser } from '../../utils/get-current-user'
import { isJourneyActive } from '../../utils/journey-utils'

export const HeaderDashboard = () => {
  // Inicializa o estado do olho a partir do localStorage ou false por padrão
  const [isEyeOpen, setIsEyeOpen] = useState(() => {
    const saved = localStorage.getItem('eyeOpen')
    return saved ? JSON.parse(saved) : false
  })
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  // Obtém o totalAmount do estado Redux
  const { totalAmount } = useSelector((state: RootState) => state.dailySales)
  
  // Obtém o estado da journey
  const { journey, conclude } = useSelector((state: RootState) => state.journey.journey)
  
  // Obtém os dados do usuário logado
  const { user } = useSelector((state: RootState) => state.auth.login)
  
  // Verifica se há journey e carrega dados iniciais
  useEffect(() => {
    const user = getCurrentUser()
    // Só faz chamada GetJourney se não há journey ativa E não há journey concluída com sucesso
    if (user?.userId && !journey && !conclude.success) {
      const userId = typeof user.userId === 'string' ? parseInt(user.userId, 10) : user.userId
      dispatch(getJourneyRequest({ userId }))
    }
  }, [dispatch, journey, conclude.success])

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false)
      }
    }

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserDropdownOpen])

  const handleLogout = () => {
    // Primeiro limpa dados localmente para evitar conflitos
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    sessionStorage.clear()
    
    // Depois dispatch da ação Redux
    dispatch(logOut())
    
    // Por fim, navega para login
    navigate(paths.authentication.login, { replace: true })
  }

  // Função para alternar visibilidade e salvar no localStorage
  const toggleEyeVisibility = () => {
    const newState = !isEyeOpen
    setIsEyeOpen(newState)
    localStorage.setItem('eyeOpen', JSON.stringify(newState))
  }

  // Função para formatar o valor monetário
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  // Determina o que exibir baseado no estado do olho
  const getDisplayValue = () => {
    if (isEyeOpen) {
      return formatCurrency(totalAmount)
    }
    return texts.hiddenBalance
  }

  // Gera a saudação personalizada com o nome do usuário
  const getUserGreeting = () => {
    if (user?.name) {
      return `Hola, ${user.name}`
    }
    return texts.userGreeting
  }

  return (
    <div className={styles.container} style={{ backgroundColor: colors.headerBackground }}>
      <div className={styles.headerTop}>
        <p className={styles.userText}>{getUserGreeting()}</p>
        <div className="flex items-center justify-end gap-2">
          <Notification className="cursor-pointer" />
          <div className="relative" ref={dropdownRef}>
            <div 
              className="w-8 h-8 bg-gray-600 rounded cursor-pointer flex items-center justify-center"
              onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            
            {isUserDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Cerrar Sesión
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.salesCard} style={{ borderBottomColor: colors.borderOrange }}>
        <div className={styles.salesInfo}>
          <p className={styles.salesLabel}>{texts.salesLabel}</p>
          <p className={styles.salesValue}>
            {getDisplayValue()}
          </p>
        </div>
        <div
          className={styles.eyeContainer}
          style={{ backgroundColor: colors.eyeContainerBackground }}
          onClick={toggleEyeVisibility}
        >
          {isEyeOpen ? (
            <Eye color={colors.eyeIcon} width={32} height={32} />
          ) : (
            <EyeClosed color={colors.eyeIcon} width={32} height={32} />
          )}
        </div>
      </div>
    </div>
  )
}
